/**
 * CateringMenu — Phase 101er (Feb 2026)
 *
 * Admin CRUD du catalogue traiteur (indépendant du catalogue barquettes).
 * Items groupés par catégorie : entrées, plats, accompagnements, desserts,
 * buffets composés, prestations. Les buffets ont une liste de composants
 * (autres items du catalogue) pour permettre la préparation cuisine.
 *
 * Alimente le sélecteur produit des devis traiteur (CateringQuoteDetail).
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Save, Trash2, Utensils, ChefHat, Boxes, Cookie, IceCream, Wrench, X } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";

const CATEGORIES = [
  { key: "entree", label: "Entrées", icon: Cookie, color: "#88A066" },
  { key: "plat", label: "Plats (sans accompagnement)", icon: ChefHat, color: "#1F4E3D" },
  { key: "accompagnement", label: "Accompagnements", icon: Boxes, color: "#8C6B2F" },
  { key: "dessert", label: "Desserts", icon: IceCream, color: "#C1A063" },
  { key: "buffet", label: "Buffets composés", icon: Utensils, color: "#5B3A8C" },
  { key: "prestation", label: "Prestations", icon: Wrench, color: "#575A57" },
];

const PORTION_UNITS = ["pcs", "pers", "portion", "kg", "g", "L", "h", "forfait"];
const VAT_RATES = [0, 2.6, 3.8, 8.1];

const emptyForm = {
  sku: "",
  name: "",
  category: "plat",
  portion_size: "",
  portion_unit: "pcs",
  default_price: "",
  vat_rate: 2.6,
  description: "",
  linked_product_id: null,
  components: [],
};

const fmtCHF = (n) => `${Number(n || 0).toFixed(2)} CHF`;

export default function CateringMenu() {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get("/catering-menu-items?include_archived=false"),
        api.get("/products"),
      ]);
      setItems(r1.data);
      setProducts(r2.data);
    } catch (e) {
      toast.error("Erreur de chargement : " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const map = {};
    for (const c of CATEGORIES) map[c.key] = [];
    for (const it of items) {
      if (map[it.category]) map[it.category].push(it);
    }
    return map;
  }, [items]);

  const itemsByIdName = useMemo(() => {
    const m = {};
    for (const it of items) m[it.id] = it;
    return m;
  }, [items]);

  const openCreate = (category = "plat") => {
    setEditingId(null);
    setForm({ ...emptyForm, category });
    setShowForm(true);
  };

  const openEdit = (item) => {
    setEditingId(item.id);
    setForm({
      sku: item.sku || "",
      name: item.name || "",
      category: item.category || "plat",
      portion_size: item.portion_size ?? "",
      portion_unit: item.portion_unit || "pcs",
      default_price: item.default_price ?? "",
      vat_rate: item.vat_rate ?? 2.6,
      description: item.description || "",
      linked_product_id: item.linked_product_id || null,
      components: (item.components || []).map((c) => ({ ...c })),
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Nom obligatoire");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        sku: form.sku.trim(),
        name: form.name.trim(),
        category: form.category,
        portion_size: form.portion_size === "" ? null : Number(form.portion_size),
        portion_unit: form.portion_unit,
        default_price: Number(form.default_price || 0),
        vat_rate: Number(form.vat_rate ?? 2.6),
        description: form.description.trim(),
        linked_product_id: form.linked_product_id || null,
        components: form.category === "buffet" ? form.components.filter((c) => c.item_id) : [],
      };
      if (editingId) {
        await api.put(`/catering-menu-items/${editingId}`, payload);
        toast.success("Item mis à jour");
      } else {
        await api.post("/catering-menu-items", payload);
        toast.success("Item créé");
      }
      closeForm();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const archive = async (item) => {
    if (!window.confirm(`Archiver « ${item.name} » ? (Il disparaîtra du sélecteur des devis mais restera dans les devis existants.)`)) return;
    try {
      await api.delete(`/catering-menu-items/${item.id}`);
      toast.success("Item archivé");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const addComponent = () => {
    setForm((f) => ({
      ...f,
      components: [...(f.components || []), { item_id: "", quantity: 1, notes: "" }],
    }));
  };

  const updateComponent = (idx, patch) => {
    setForm((f) => ({
      ...f,
      components: f.components.map((c, i) => i === idx ? { ...c, ...patch } : c),
    }));
  };

  const removeComponent = (idx) => {
    setForm((f) => ({
      ...f,
      components: f.components.filter((_, i) => i !== idx),
    }));
  };

  // Composants disponibles pour un buffet = tous les items sauf autres buffets + non archivés
  const availableComponents = useMemo(() => {
    return items.filter((it) => it.category !== "buffet" && !it.archived);
  }, [items]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-[#575A57] p-6" data-testid="catering-menu-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Chargement du catalogue traiteur…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="catering-menu-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-heading font-semibold flex items-center gap-2">
            <Utensils className="w-6 h-6 text-[#8C6B2F]" /> Carte traiteur
          </h1>
          <p className="text-sm text-[#575A57] mt-1 max-w-2xl">
            Catalogue dédié aux devis traiteur — indépendant des barquettes.
            Entrées, plats, accompagnements, desserts, buffets composés et prestations.
          </p>
        </div>
        <Button
          onClick={() => openCreate("plat")}
          className="bg-[#8C6B2F] hover:bg-[#735524] text-white"
          data-testid="add-catering-item"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Nouvel item
        </Button>
      </div>

      {CATEGORIES.map((cat) => {
        const catItems = grouped[cat.key] || [];
        const Icon = cat.icon;
        return (
          <Card key={cat.key} className="border-[#E5E5E0]" data-testid={`catering-cat-${cat.key}`}>
            <CardContent className="p-5">
              <div className="flex items-baseline justify-between mb-3 gap-2 flex-wrap">
                <h2 className="text-sm font-semibold uppercase tracking-wider flex items-center gap-1.5" style={{ color: cat.color }}>
                  <Icon className="w-4 h-4" /> {cat.label}
                  <span className="text-xs text-[#A3A6A3] font-normal">({catItems.length})</span>
                </h2>
                <Button size="sm" variant="outline" onClick={() => openCreate(cat.key)} data-testid={`add-in-cat-${cat.key}`}>
                  <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter
                </Button>
              </div>
              {catItems.length === 0 ? (
                <p className="text-xs text-[#A3A6A3] italic">Aucun item dans cette catégorie.</p>
              ) : (
                <div className="space-y-1.5">
                  {catItems.map((it) => (
                    <div
                      key={it.id}
                      className="grid grid-cols-12 gap-2 items-center bg-[#FAFAF7] border border-[#E5E5E0] rounded p-2 hover:border-[#8C6B2F] transition-colors"
                      data-testid={`catering-item-${it.id}`}
                    >
                      <div className="col-span-12 md:col-span-6 flex items-baseline gap-2 flex-wrap">
                        <span className="font-medium text-[#1A1C1A]">{it.name}</span>
                        {it.sku && <Badge variant="outline" className="text-[10px] font-mono">{it.sku}</Badge>}
                        {it.linked_product_id && (
                          <Badge className="bg-[#F1EAF8] text-[#5B3A8C] border-[#C9B5E0] text-[9px]">
                            ↔ {products.find((p) => p.id === it.linked_product_id)?.name || "produit lié"}
                          </Badge>
                        )}
                        {cat.key === "buffet" && (it.components || []).length > 0 && (
                          <Badge className="bg-[#F8F4FD] text-[#5B3A8C] border-[#C9B5E0] text-[9px]">
                            {it.components.length} composant{it.components.length > 1 ? "s" : ""}
                          </Badge>
                        )}
                      </div>
                      <div className="col-span-6 md:col-span-2 text-xs text-[#575A57] font-mono">
                        {it.portion_size ? `${it.portion_size} ${it.portion_unit}` : it.portion_unit}
                      </div>
                      <div className="col-span-4 md:col-span-2 text-right font-mono font-semibold text-[#1F4E3D]">
                        {fmtCHF(it.default_price)}
                      </div>
                      <div className="col-span-2 md:col-span-2 flex justify-end gap-1">
                        <Button size="sm" variant="outline" onClick={() => openEdit(it)} data-testid={`edit-item-${it.id}`}>
                          Éditer
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => archive(it)} className="text-[#C05C47] hover:bg-[#FCEEEA]" data-testid={`archive-item-${it.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-6 overflow-y-auto" onClick={closeForm}>
          <Card className="w-full max-w-2xl border-[#E5E5E0]" onClick={(e) => e.stopPropagation()} data-testid="catering-item-form">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold">{editingId ? "Modifier l'item" : "Nouvel item"}</h2>
                <button onClick={closeForm} className="text-[#575A57] hover:text-[#1A1C1A]" data-testid="close-item-form">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Nom *</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="form-name" placeholder="Ex: Rouleau de printemps" />
                </div>
                <div>
                  <Label className="text-xs">SKU (code court)</Label>
                  <Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} data-testid="form-sku" placeholder="Ex: ENT-01" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Catégorie *</Label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value, components: e.target.value === "buffet" ? form.components : [] })}
                    className="w-full border border-[#E5E5E0] rounded px-2 py-1.5 text-sm bg-white"
                    data-testid="form-category"
                  >
                    {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Prix suggéré (CHF)</Label>
                  <Input
                    type="number"
                    step="0.05"
                    value={form.default_price}
                    onChange={(e) => setForm({ ...form, default_price: e.target.value })}
                    data-testid="form-price"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Portion (quantité)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={form.portion_size}
                    onChange={(e) => setForm({ ...form, portion_size: e.target.value })}
                    data-testid="form-portion-size"
                    placeholder="Ex: 150"
                  />
                </div>
                <div>
                  <Label className="text-xs">Unité</Label>
                  <select
                    value={form.portion_unit}
                    onChange={(e) => setForm({ ...form, portion_unit: e.target.value })}
                    className="w-full border border-[#E5E5E0] rounded px-2 py-1.5 text-sm bg-white"
                    data-testid="form-portion-unit"
                  >
                    {PORTION_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">TVA</Label>
                  <select
                    value={form.vat_rate}
                    onChange={(e) => setForm({ ...form, vat_rate: Number(e.target.value) })}
                    className="w-full border border-[#E5E5E0] rounded px-2 py-1.5 text-sm bg-white"
                    data-testid="form-vat"
                  >
                    {VAT_RATES.map((v) => <option key={v} value={v}>{v}%</option>)}
                  </select>
                </div>
              </div>

              <div>
                <Label className="text-xs">Description (optionnel)</Label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={2}
                  className="w-full border border-[#E5E5E0] rounded px-2 py-1.5 text-sm bg-white"
                  data-testid="form-description"
                  placeholder="Détails de composition, allergènes, etc."
                />
              </div>

              <div>
                <Label className="text-xs">Barquette liée (thesaurus, optionnel)</Label>
                <select
                  value={form.linked_product_id || ""}
                  onChange={(e) => setForm({ ...form, linked_product_id: e.target.value || null })}
                  className="w-full border border-[#E5E5E0] rounded px-2 py-1.5 text-sm bg-white"
                  data-testid="form-linked-product"
                >
                  <option value="">— Aucun lien —</option>
                  {products.filter((p) => !p.archived).map((p) => (
                    <option key={p.id} value={p.id}>{p.name} ({p.sku})</option>
                  ))}
                </select>
                <p className="text-[10px] text-[#A3A6A3] mt-1">
                  Permet de retrouver la recette / allergènes / coût de la barquette sans dupliquer.
                </p>
              </div>

              {/* Composants pour les buffets */}
              {form.category === "buffet" && (
                <div className="border-t border-[#E5E5E0] pt-3">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs font-semibold">Composants du buffet</Label>
                    <Button size="sm" variant="outline" onClick={addComponent} data-testid="add-component">
                      <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter un composant
                    </Button>
                  </div>
                  <p className="text-[10px] text-[#A3A6A3] mb-2">
                    Le devis servira aussi de bon de préparation cuisine — décomposez le buffet en items atomiques.
                  </p>
                  {form.components.length === 0 ? (
                    <p className="text-xs text-[#A3A6A3] italic">Aucun composant. Cliquez sur « Ajouter » pour composer le buffet.</p>
                  ) : (
                    <div className="space-y-2">
                      {form.components.map((c, idx) => (
                        <div key={idx} className="grid grid-cols-12 gap-2 items-center bg-[#F1EAF8]/40 border border-[#C9B5E0] rounded p-2" data-testid={`component-${idx}`}>
                          <div className="col-span-7">
                            <select
                              value={c.item_id}
                              onChange={(e) => updateComponent(idx, { item_id: e.target.value })}
                              className="w-full border border-[#E5E5E0] rounded px-2 py-1 text-xs bg-white"
                              data-testid={`component-item-${idx}`}
                            >
                              <option value="">— Sélectionner un item —</option>
                              {CATEGORIES.filter((cat) => cat.key !== "buffet").map((cat) => (
                                <optgroup key={cat.key} label={cat.label}>
                                  {availableComponents.filter((it) => it.category === cat.key).map((it) => (
                                    <option key={it.id} value={it.id}>{it.name}</option>
                                  ))}
                                </optgroup>
                              ))}
                            </select>
                          </div>
                          <div className="col-span-2">
                            <Input
                              type="number"
                              step="0.5"
                              value={c.quantity}
                              onChange={(e) => updateComponent(idx, { quantity: Number(e.target.value) })}
                              placeholder="Qté"
                              className="text-right text-xs"
                              data-testid={`component-qty-${idx}`}
                            />
                          </div>
                          <div className="col-span-2">
                            <Input
                              value={c.notes || ""}
                              onChange={(e) => updateComponent(idx, { notes: e.target.value })}
                              placeholder="Notes"
                              className="text-xs"
                              data-testid={`component-notes-${idx}`}
                            />
                          </div>
                          <button
                            onClick={() => removeComponent(idx)}
                            className="col-span-1 text-[#C05C47] hover:bg-[#FCEEEA] rounded p-1 inline-flex justify-center"
                            data-testid={`remove-component-${idx}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-[#E5E5E0]">
                <Button variant="outline" onClick={closeForm} data-testid="cancel-item-form">Annuler</Button>
                <Button onClick={save} disabled={saving} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="save-item-form">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />}
                  {editingId ? "Enregistrer" : "Créer"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
